import React from "react";

import IconButton from "@material-ui/core/IconButton";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";

import { CH_KEYS, CH } from "./tokens";

// Cabeçalho da tela por variante. Estilos inline pixel-perfect (README §1a/1b/1c).
// Botão "Gerenciar estágios" é gated por `canManageStages`.

const VARIANT_META = {
  sunlit: {
    title: "Pipeline de vendas",
    subtitle: "Kanban de leads · WhatsApp, Instagram e Site",
    titleStyle: { fontSize: 21, fontWeight: 900, color: "#33302B" },
    subtitleStyle: { fontSize: 12.5, color: "#8B8577", marginTop: 1 },
    ghostBtn: {
      background: "#fff",
      border: "1px solid #E8DFCE",
      color: "#6B6456",
      borderRadius: 999,
      padding: "9px 16px",
      fontWeight: 800,
      fontSize: 12.5,
    },
    primaryBtn: {
      background: "#F2653A",
      border: "none",
      color: "#fff",
      borderRadius: 999,
      padding: "10px 18px",
      fontWeight: 800,
      fontSize: 13,
      boxShadow: "0 2px 6px rgba(242,101,58,.35)",
    },
    input: {
      width: 290,
      background: "#fff",
      border: "1px solid #E8DFCE",
      borderRadius: 999,
      padding: "9px 16px",
      fontSize: 13,
      color: "#33302B",
    },
    select: {
      background: "#fff",
      border: "1px solid #E8DFCE",
      borderRadius: 999,
      padding: "9px 14px",
      fontSize: 12.5,
      fontWeight: 700,
      color: "#6B6456",
    },
    gear: "#6B6456",
  },
  funnel: {
    title: "CRM Kanban",
    subtitle: "Funil comercial · atualizado agora",
    titleStyle: { fontSize: 20, fontWeight: 800, color: "#151B26" },
    subtitleStyle: { fontSize: 12.5, color: "#7A8496", marginTop: 1 },
    ghostBtn: {
      background: "#fff",
      border: "1px solid #E2E7EE",
      color: "#3D4756",
      borderRadius: 9,
      padding: "9px 15px",
      fontWeight: 600,
      fontSize: 12.5,
    },
    primaryBtn: {
      background: "#4A6CF7",
      border: "none",
      color: "#fff",
      borderRadius: 9,
      padding: "10px 17px",
      fontWeight: 700,
      fontSize: 13,
    },
    input: {
      width: 280,
      background: "#fff",
      border: "1px solid #E2E7EE",
      borderRadius: 8,
      padding: "8px 13px",
      fontSize: 13,
      color: "#151B26",
    },
    select: {
      background: "#fff",
      border: "1px solid #E2E7EE",
      borderRadius: 8,
      padding: "8px 11px",
      fontSize: 12.5,
      fontWeight: 600,
      color: "#3D4756",
    },
    gear: "#3D4756",
  },
  soft: {
    title: "Funil de relacionamento",
    subtitle: "Leads por estágio · tempo de resposta ao vivo",
    titleStyle: { fontSize: 21, fontWeight: 800, color: "#3D3350" },
    subtitleStyle: { fontSize: 12.5, color: "#7A6C8A", marginTop: 1 },
    ghostBtn: {
      background: "rgba(255,255,255,.65)",
      border: "1px solid rgba(255,255,255,.9)",
      color: "#5A4B6E",
      borderRadius: 11,
      padding: "9px 15px",
      fontWeight: 700,
      fontSize: 12.5,
    },
    primaryBtn: {
      background: "#7C5CF0",
      border: "none",
      color: "#fff",
      borderRadius: 11,
      padding: "10px 17px",
      fontWeight: 700,
      fontSize: 13,
      boxShadow: "0 3px 10px rgba(124,92,240,.35)",
    },
    input: {
      width: 290,
      background: "rgba(255,255,255,.85)",
      border: "1px solid rgba(255,255,255,.95)",
      borderRadius: 10,
      padding: "9px 14px",
      fontSize: 13,
      color: "#3D3350",
    },
    select: {
      background: "rgba(255,255,255,.85)",
      border: "1px solid rgba(255,255,255,.95)",
      borderRadius: 10,
      padding: "9px 12px",
      fontSize: 12.5,
      fontWeight: 700,
      color: "#5A4B6E",
    },
    gear: "#5A4B6E",
  },
};

const btnBase = { fontFamily: "inherit", cursor: "pointer" };
const fieldBase = { fontFamily: "inherit", outline: "none" };

const Filters = ({ meta, filters, onFilter, attendants }) => (
  <>
    <input
      value={filters.q}
      onChange={(e) => onFilter({ q: e.target.value })}
      placeholder="Buscar lead, telefone ou tag…"
      style={{ ...fieldBase, flex: "none", ...meta.input }}
    />
    <select
      value={filters.ch}
      onChange={(e) => onFilter({ ch: e.target.value })}
      style={{ ...fieldBase, cursor: "pointer", ...meta.select }}
    >
      <option value="">Todos os canais</option>
      {CH_KEYS.map((k) => (
        <option key={k} value={k}>
          {CH[k][0]}
        </option>
      ))}
    </select>
    <select
      value={filters.ag}
      onChange={(e) => onFilter({ ag: e.target.value })}
      style={{ ...fieldBase, cursor: "pointer", ...meta.select }}
    >
      <option value="">Todos atendentes</option>
      {attendants.map((name) => (
        <option key={name} value={name}>
          {name}
        </option>
      ))}
    </select>
  </>
);

// `section`: "header" renderiza título+botões; "filters" renderiza a barra de
// filtros. Separado p/ o orquestrador poder intercalar as métricas entre eles.
const KanbanToolbar = ({
  variant,
  section = "header",
  funnels = [],
  funnelId,
  onFunnelChange,
  filters,
  onFilter,
  attendants,
  canManageStages,
  onManageStages,
  onNewLead,
  onOpenSettings,
  activeLeadsCount,
}) => {
  const meta = VARIANT_META[variant] || VARIANT_META.funnel;

  const filtersBar = renderFilters(
    variant,
    meta,
    filters,
    onFilter,
    attendants,
    activeLeadsCount,
  );

  if (section === "filters") return filtersBar;

  const buttonsAndGear = (
    <>
      {funnels.length > 0 && (
        <select
          value={funnelId || ""}
          onChange={(e) => onFunnelChange(Number(e.target.value))}
          title="Funil"
          style={{ ...fieldBase, cursor: "pointer", ...meta.select }}
        >
          {funnels.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      )}
      {canManageStages && (
        <button
          type="button"
          onClick={onManageStages}
          style={{ ...btnBase, ...meta.ghostBtn }}
        >
          Gerenciar estágios
        </button>
      )}
      <button
        type="button"
        onClick={onNewLead}
        style={{ ...btnBase, ...meta.primaryBtn }}
      >
        + Novo lead
      </button>
      <IconButton
        size="small"
        onClick={onOpenSettings}
        style={{ color: meta.gear }}
      >
        <SettingsOutlinedIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ flex: 1 }}>
        <div style={meta.titleStyle}>{meta.title}</div>
        <div style={meta.subtitleStyle}>{meta.subtitle}</div>
      </div>
      {buttonsAndGear}
    </div>
  );
};

// Barra de filtros por variante.
function renderFilters(
  variant,
  meta,
  filters,
  onFilter,
  attendants,
  activeCount,
) {
  const inner = (
    <Filters
      meta={meta}
      filters={filters}
      onFilter={onFilter}
      attendants={attendants}
    />
  );

  if (variant === "funnel") {
    return (
      <div
        style={{
          background: "#F4F6F9",
          borderRadius: 10,
          padding: 8,
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        {inner}
        <span style={{ flex: 1 }} />
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 600,
            color: "#98A2B3",
            paddingRight: 8,
          }}
        >
          {activeCount} leads ativos
        </span>
      </div>
    );
  }

  if (variant === "sunlit") {
    return (
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        {inner}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: "#A79E8C" }}>
          Arraste cards entre colunas
        </span>
      </div>
    );
  }

  // soft
  return (
    <div
      style={{ display: "flex", gap: 10, marginTop: 14, alignItems: "center" }}
    >
      {inner}
    </div>
  );
}

export default KanbanToolbar;
